import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { stockActions } from '../../actions';
import preferenceAction from '../../actions/preference.action';
import LoadingImage from '../../components/general/LoadingImage';
import { categoryToRegin } from '../../components/stock/stockCategory';
import StockCategoryRankingTable from '../../components/stock/StockCategoryRankingTable';
import StockStrategy from '../../components/stock/stockStrategy';
import StockTemporalTable from '../../components/stock/StockTemporalTable';
import CreateOrEditTipsModal from '../../components/tips/CreateOrEditTipsModal';
import CreateTipsButton from '../../components/tips/CreateTipsButton';
import TipsTemporalTable from '../../components/tips/TipsTemporalTable';
import { useErrorMessage } from '../../errors';
import useDispatch from '../../hooks/useDispatch';
import { usePreferences } from '../../hooks/usePreferences';
import { useUser } from '../../hooks/users';
import useSelector from '../../hooks/useSelector';
import { StockLookupEntity } from '../../py-generated/datalouder/stock/types';
import { miscService } from '../../services';
import { tipsService } from '../../services';
import BlurPlaceholder from '../FreeUser/BlurPlaceholder';
import TutorialQuestionMark from '../Help/TutorialQuestionMark';
import PlanCards from '../User/PlanCards';

interface Props {
  stock: StockLookupEntity;
}

export const STRATEGY_TAB_KEY = 'strategy';
export const TIPS_TAB_KEY = 'expert-views';
const INDUSTRY_KEY = 'category';
const SUB_INDUSTRY_KEY = 'subcategory';
const STAT_HIST_KEY = 'stat-history';

const MEMORIZABLE_TAB_KEYS = new Set([
  STRATEGY_TAB_KEY,
  TIPS_TAB_KEY,
  INDUSTRY_KEY,
  SUB_INDUSTRY_KEY,
  STAT_HIST_KEY,
]);

const FALLBACK_DEFAULT_TAB_KEY = TIPS_TAB_KEY;
const getDefaultTabKey = (
  priceViewerLabelType: string,
  showStrategyTab: boolean,
  showIndustriesTab: boolean,
  hasMembership: boolean
) => {
  if (!hasMembership) {
    return STAT_HIST_KEY;
  }
  if (!MEMORIZABLE_TAB_KEYS.has(priceViewerLabelType)) {
    return FALLBACK_DEFAULT_TAB_KEY;
  }
  if (priceViewerLabelType === STRATEGY_TAB_KEY && !showStrategyTab) {
    return FALLBACK_DEFAULT_TAB_KEY;
  }
  if (
    ['category', 'subcategoy'].includes(priceViewerLabelType) &&
    !showIndustriesTab
  ) {
    return FALLBACK_DEFAULT_TAB_KEY;
  }
  return priceViewerLabelType;
};

// Created to reduce Cognitive Complexity
const CreateTipsModalWrapper: React.FC<{
  show: boolean;
  onHide: () => void;
  onSubmit: () => void;
  createWithStock?: StockLookupEntity;
}> = ({ show, createWithStock, onHide, onSubmit }) => {
  return createWithStock ? (
    <CreateOrEditTipsModal
      show={show}
      createWithStock={createWithStock}
      onHide={onHide}
      onSubmit={onSubmit}
    />
  ) : null;
};

const StockPageStatisticsTable: React.FC<Props> = ({ stock }) => {
  const { t, i18n } = useTranslation();

  const [showCreateTipsModal, setCreateTipsModalState] =
    useState<boolean>(false);
  const [selectedKey, selectKey] = useState<string>();
  const user = useUser();
  const hasMembership = user?.hasMembership;
  const dispatch = useDispatch();

  const { data: tips, isLoading: loadingTips } = useQuery(
    ['fetchTips', stock.stockCode],
    () => tipsService.getTips(stock.stockCode),
    {
      enabled: !!user,
    }
  );

  const { data: strategyResult, error: strategyError } = useQuery(
    ['getNopStrategyResult', stock.stockCode],
    () => miscService.getNopStrategyResult(stock.stockCode),
    { enabled: hasMembership }
  );

  const getErrorMessage = useErrorMessage();

  const { loading: loadingRelated } = useSelector(
    state => state.stock.lookupRelated
  );

  // reset the selected key when stock changes
  useEffect(() => {
    selectKey(null);
  }, [stock]);

  const { priceViewerLabelType } = usePreferences();
  const showStrategyTab =
    strategyResult !== undefined &&
    strategyResult.length > 0 &&
    hasMembership &&
    stock.category === 'HK';
  const showIndustryTabs = !!stock.industry;

  const activeKey =
    selectedKey ??
    getDefaultTabKey(
      priceViewerLabelType,
      showStrategyTab,
      showIndustryTabs,
      hasMembership
    );

  useEffect(() => {
    if (hasMembership && stock.category && stock.industry) {
      dispatch(
        stockActions.fetchLookupRelated(
          i18n.language,
          stock.category,
          stock.industry[0].id,
          stock.industry[1].id
        )
      );
    }
  }, [dispatch, hasMembership, i18n.language, stock.category, stock.industry]);

  const handleSelect = useCallback(
    (key: string) => {
      dispatch(preferenceAction.setPriceViewerLabelType(key));
      selectKey(key);
    },
    [dispatch]
  );

  return (
    <>
      <CreateTipsModalWrapper
        show={showCreateTipsModal}
        createWithStock={stock}
        onHide={() => {
          setCreateTipsModalState(false);
        }}
        onSubmit={() => {
          setCreateTipsModalState(false);
        }}
      />
      <Tabs
        style={{ margin: 'unset' }}
        activeKey={activeKey}
        mountOnEnter
        onSelect={handleSelect}
      >
        <Tab
          eventKey={TIPS_TAB_KEY}
          title={
            <span className="expert-views-tab">
              {t('expert-views')}{' '}
              <TutorialQuestionMark tutorialId={38} alwaysVisible />
              <LoadingImage loading={loadingTips} />
            </span>
          }
        >
          {user ? (
            <>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 10,
                }}
              >
                <CreateTipsButton
                  small
                  onClick={() => {
                    setCreateTipsModalState(true);
                  }}
                />
              </div>
              <TipsTemporalTable data={tips} showInfluencerCol />
            </>
          ) : (
            <>
              <h6>{t('register-to-see-more')}</h6>
              <PlanCards />
            </>
          )}
        </Tab>
        {stock.industry && (
          <Tab
            eventKey={INDUSTRY_KEY}
            title={
              <span>
                {stock.industry[0].name[i18n.language]}
                <LoadingImage loading={loadingRelated} />
              </span>
            }
          >
            <BlurPlaceholder featureKey="peer-table">
              <StockCategoryRankingTable
                level="category"
                region={categoryToRegin(stock.category)}
              />
            </BlurPlaceholder>
          </Tab>
        )}
        {stock.industry && (
          <Tab
            eventKey={SUB_INDUSTRY_KEY}
            title={
              <span>
                {stock.industry[1].name[i18n.language]}
                <LoadingImage loading={loadingRelated} />
              </span>
            }
          >
            <BlurPlaceholder featureKey="peer-table">
              <StockCategoryRankingTable
                level="subCategory"
                region={categoryToRegin(stock.category)}
              />
            </BlurPlaceholder>
          </Tab>
        )}
        {stock.category && (
          <Tab
            eventKey={STAT_HIST_KEY}
            title={
              <span>
                {t('recent-month')}
                <LoadingImage loading={loadingRelated} />
              </span>
            }
          >
            <StockTemporalTable
              region={categoryToRegin(stock.category)}
              category={stock.category}
              ticker={stock.stockCode}
            />
          </Tab>
        )}
        {showStrategyTab && (
          <Tab eventKey={STRATEGY_TAB_KEY} title={t('strategy')}>
            {strategyError ? getErrorMessage(strategyError) : ''}
            <StockStrategy
              stockCode={stock.stockCode}
              strategyResult={strategyResult}
            />
          </Tab>
        )}
        {!user && (
          <Tab eventKey="register" title={t('related-statistics')}>
            <h6>{t('register-to-see-more')}</h6>
            <PlanCards />
          </Tab>
        )}
      </Tabs>
    </>
  );
};

export default StockPageStatisticsTable;
